/**
 * Welcome popup — first-visit email capture.
 * On submit: POSTs email to Google Apps Script, adds mini-comb to cart, shows thanks state.
 */

import { CartAddEvent } from '@theme/events';

const APPS_SCRIPT_URL = 'https://script.google.com/macros/s/AKfycbx3vPSj6rXlmxzB1YQaG2TSmj3kKlS4gxnEume6oxcDliyA1NqNqqoh6sNgZZxIhknZ/exec';

class WelcomePopup extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('[ref="dialog"]');
    this.form = this.querySelector('[data-form]');
    this.emailInput = this.querySelector('#welcome-popup-email');
    this.errorEl = this.querySelector('[data-error]');
    this.submitBtn = this.querySelector('[data-submit]');
    this.submitLabel = this.querySelector('[data-submit-label]');
    this.spinner = this.querySelector('[data-spinner]');
    this.formView = this.querySelector('[data-view="form"]');
    this.thanksView = this.querySelector('[data-view="thanks"]');
    this.giftVariantId = this.dataset.giftVariantId;
    this.giftHandle = this.dataset.giftHandle;
    this.storageKey = this.dataset.storageKey || 'welcome-popup-seen';
    this.delay = Number(this.dataset.delay) || 2000;

    this.querySelectorAll('[data-close]').forEach((el) =>
      el.addEventListener('click', () => this.#close())
    );
    // Esc key — let browser close natively, just mark as seen
    this.dialog.addEventListener('close', () => this.#markSeen());
    // Backdrop click — close when clicking outside the inner box
    this.dialog.addEventListener('click', (e) => {
      if (e.target === this.dialog) this.#close();
    });
    this.form.addEventListener('submit', this.#onSubmit);

    if (this.#shouldShow()) {
      setTimeout(() => this.#checkStockAndOpen(), this.delay);
    }
  }

  #shouldShow() {
    try {
      return !localStorage.getItem(this.storageKey);
    } catch {
      return false;
    }
  }

  async #checkStockAndOpen() {
    try {
      const handle = this.giftHandle;
      if (!handle) return;
      const res = await fetch(`/products/${handle}.js`);
      if (!res.ok) return;
      const product = await res.json();
      if (!product.available) return;
      this.#open();
    } catch {
      // Product unavailable or network error — don't show popup
    }
  }

  #markSeen() {
    try {
      localStorage.setItem(this.storageKey, String(Date.now()));
    } catch {}
  }

  #open() {
    if (typeof this.dialog.showModal === 'function') {
      this.dialog.showModal();
    } else {
      this.dialog.setAttribute('open', '');
    }
  }

  #close() {
    this.#markSeen();
    if (typeof this.dialog.close === 'function') {
      this.dialog.close();
    } else {
      this.dialog.removeAttribute('open');
    }
  }

  #setLoading(isLoading) {
    this.submitBtn.disabled = isLoading;
    this.spinner.hidden = !isLoading;
    this.submitLabel.style.opacity = isLoading ? '0.5' : '1';
  }

  #showError(message) {
    this.errorEl.textContent = message;
    this.errorEl.hidden = false;
  }

  #clearError() {
    this.errorEl.textContent = '';
    this.errorEl.hidden = true;
  }

  #onSubmit = async (event) => {
    event.preventDefault();
    this.#clearError();

    const email = this.emailInput.value.trim();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      this.#showError('Please enter a valid email.');
      return;
    }

    this.#setLoading(true);

    try {
      await Promise.all([this.#submitEmail(email), this.#addGiftToCart()]);
      this.#showThanks();
      this.#markSeen();

      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (err) {
      console.error('[welcome-popup] submit failed', err);
      this.#showError('Something went wrong. Please try again.');
      this.#setLoading(false);
    }
  };

  async #submitEmail(email) {
    if (!APPS_SCRIPT_URL || APPS_SCRIPT_URL.startsWith('REPLACE_')) {
      console.warn('[welcome-popup] APPS_SCRIPT_URL not configured');
      return;
    }
    await fetch(APPS_SCRIPT_URL, {
      method: 'POST',
      mode: 'no-cors',
      headers: { 'Content-Type': 'text/plain;charset=utf-8' },
      body: JSON.stringify({ email, source: 'welcome-popup' }),
    });
  }

  async #addGiftToCart() {
    const variantId = await this.#resolveVariantId();
    if (!variantId) throw new Error('No variant available');

    /** @type {string[]} */
    const sectionIds = [];
    document.querySelectorAll('cart-items-component').forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) {
        sectionIds.push(el.dataset.sectionId);
      }
    });

    /** @type {{items: {id: number, quantity: number, properties: Record<string, string>}[], sections?: string}} */
    const body = {
      items: [
        {
          id: Number(variantId),
          quantity: 1,
          properties: { _gift: 'welcome-popup' },
        },
      ],
    };
    if (sectionIds.length) body.sections = sectionIds.join(',');

    const cartRes = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    if (!cartRes.ok) {
      const text = await cartRes.text().catch(() => '');
      throw new Error(`Add to cart failed: ${cartRes.status} ${text}`);
    }

    const response = await cartRes.json();
    this.dispatchEvent(
      new CartAddEvent({}, 'welcome-popup', {
        source: 'welcome-popup',
        itemCount: 1,
        variantId: String(variantId),
        sections: response.sections,
      })
    );
  }

  async #resolveVariantId() {
    if (this.giftVariantId) return this.giftVariantId;
    if (!this.giftHandle) return null;
    const res = await fetch(`/products/${this.giftHandle}.js`);
    if (!res.ok) return null;
    const product = await res.json();
    const variant =
      product.variants.find((/** @type {{available: boolean}} */ v) => v.available) ||
      product.variants[0];
    return variant?.id ?? null;
  }

  #showThanks() {
    this.formView.hidden = true;
    this.thanksView.hidden = false;
  }
}

if (!customElements.get('welcome-popup')) {
  customElements.define('welcome-popup', WelcomePopup);
}
